import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';

import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';
import { identifierField, secretField } from '../../utils/validation-helper';

type GatelessUnderwriteFormValues = {
  clientId: string;
  clientSecret: string;
};

interface EditGatelessUnderwriteIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (_settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditGatelessUnderwriteIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditGatelessUnderwriteIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let clientId = '';
  let clientSecret = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    clientId = data.clientId || '';
    clientSecret = data.clientSecret || '';
  }

  const validationSchema = yup.object().shape({
    clientId: identifierField('Client ID').required('Client ID is required'),
    clientSecret: secretField('Client Secret').required('Client Secret is required'),
  });

  const { control, handleSubmit, formState } = useForm<GatelessUnderwriteFormValues>({
    defaultValues: {
      clientId: clientId,
      clientSecret: clientSecret,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Gateless AUW Settings' : 'Gateless AUW Settings'}</h3>
      <form
        autoComplete="off"
        onSubmit={handleSubmit((data) => {
          if (onSave) {
            let settings: IntegrationSettings;

            if (isEditMode && integrationSettings) {
              settings = {
                ...integrationSettings,
                vendor: IntegrationVendor.Gateless,
                environment: environment,
                integrationData: JSON.stringify({
                  clientId: data.clientId,
                  clientSecret: data.clientSecret,
                }),
              };
            } else {
              settings = {
                name: 'Gateless AUW Integration',
                vendor: IntegrationVendor.Gateless,
                status: IntegrationStatus.Active,
                environment: environment,
                integrationData: JSON.stringify({
                  clientId: data.clientId,
                  clientSecret: data.clientSecret,
                }),
              };
            }

            onSave(settings);
          }
        })}
        noValidate
        className="pb-3 p-fluid p-input-filled"
      >
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="clientId"
            label="Client ID *"
            placeholder="Enter Client ID"
          />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="clientSecret"
            label="Client Secret *"
            placeholder="Enter Client Secret"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="flex justify-content-end gap-2 mt-4" style={{ zIndex: 1000 }}>
          <Button
            type="button"
            label="Cancel"
            className="p-button p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
